import React, { useState, useEffect } from 'react';
import { 
  XIcon, 
  PlusIcon, 
  TrashIcon, 
  CalculatorIcon,
  UserIcon,
  CalendarIcon
} from 'lucide-react';
import { billingAPI, patientsApi } from '../../lib/api';

interface BillItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

interface Patient {
  id: string;
  patientNumber: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface CreateBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedPatientId?: string;
}

const CreateBillingModal: React.FC<CreateBillingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedPatientId
}) => {
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [formData, setFormData] = useState({
    patientId: preselectedPatientId || '',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    tax: 0,
    discount: 0,
    notes: ''
  });
  const [billItems, setBillItems] = useState<BillItem[]>([
    { description: '', quantity: 1, unitPrice: 0 }
  ]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      loadPatients();
      if (preselectedPatientId) {
        setFormData(prev => ({ ...prev, patientId: preselectedPatientId }));
      }
    }
  }, [isOpen, preselectedPatientId]);

  const loadPatients = async () => {
    try {
      setPatientsLoading(true);
      const response = await patientsApi.getPatients({ limit: 100 });
      if (response.data.success) {
        setPatients(response.data.data?.patients || []);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setPatientsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.patientId) {
      newErrors.patientId = 'Patient is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    // Validate bill items
    if (billItems.length === 0 || billItems.every(item => !item.description.trim())) {
      newErrors.billItems = 'At least one billing item is required';
    }

    billItems.forEach((item, index) => {
      if (item.description.trim() && (item.quantity <= 0 || item.unitPrice < 0)) {
        newErrors[`item_${index}`] = 'Valid quantity and price required';
      }
    });

    if (formData.tax < 0 || formData.tax > 100) {
      newErrors.tax = 'Tax must be between 0 and 100%';
    }

    if (formData.discount < 0 || formData.discount > 100) {
      newErrors.discount = 'Discount must be between 0 and 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotals = () => {
    const subtotal = billItems.reduce((sum, item) => 
      sum + (item.quantity * item.unitPrice), 0);
    const taxAmount = (subtotal * formData.tax) / 100;
    const discountAmount = (subtotal * formData.discount) / 100;
    const total = subtotal + taxAmount - discountAmount;

    return {
      subtotal,
      taxAmount,
      discountAmount,
      total: Math.max(0, total)
    };
  };

  const addBillItem = () => {
    setBillItems([...billItems, { description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeBillItem = (index: number) => {
    if (billItems.length > 1) {
      setBillItems(billItems.filter((_, i) => i !== index));
    }
  };

  const updateBillItem = (index: number, field: keyof BillItem, value: string | number) => {
    const updatedItems = [...billItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setBillItems(updatedItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setErrors({});

      const validItems = billItems.filter(item => 
        item.description.trim() && item.quantity > 0 && item.unitPrice >= 0
      );

      const response = await billingAPI.createBilling({
        patientId: formData.patientId,
        items: validItems,
        tax: formData.tax,
        discount: formData.discount,
        dueDate: formData.dueDate,
        notes: formData.notes || undefined
      });

      if (response.success) {
        onSuccess();
        onClose();
        resetForm();
      } else {
        setErrors({ submit: 'Failed to create billing record' });
      }
    } catch (error) {
      console.error('Error creating billing:', error);
      setErrors({ submit: 'Failed to create billing record' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      tax: 0,
      discount: 0,
      notes: ''
    });
    setBillItems([{ description: '', quantity: 1, unitPrice: 0 }]);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const totals = calculateTotals();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <CalculatorIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Create Invoice</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Patient Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserIcon className="h-4 w-4 inline mr-1" />
                  Patient *
                </label>
                <select
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  className={`input ${errors.patientId ? 'border-red-500' : ''}`}
                  disabled={patientsLoading || !!preselectedPatientId}
                >
                  <option value="">Select a patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.user.firstName} {patient.user.lastName} ({patient.patientNumber})
                    </option>
                  ))}
                </select>
                {errors.patientId && <p className="text-red-500 text-sm mt-1">{errors.patientId}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Due Date *
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className={`input ${errors.dueDate ? 'border-red-500' : ''}`}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>
            </div>

            {/* Bill Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Billing Items</h3>
                <button
                  type="button"
                  onClick={addBillItem}
                  className="btn-secondary text-sm"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Item
                </button>
              </div>

              <div className="space-y-3">
                {billItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 border border-gray-200 rounded-lg">
                    <div className="md:col-span-5">
                      <input
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateBillItem(index, 'description', e.target.value)}
                        className={`input ${errors[`item_${index}`] ? 'border-red-500' : ''}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <input
                        type="number"
                        placeholder="Qty"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateBillItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        className={`input ${errors[`item_${index}`] ? 'border-red-500' : ''}`}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <input
                        type="number"
                        placeholder="Unit Price"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateBillItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className={`input ${errors[`item_${index}`] ? 'border-red-500' : ''}`}
                      />
                    </div>
                    <div className="md:col-span-1 flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        ${(item.quantity * item.unitPrice).toFixed(2)}
                      </span>
                    </div>
                    <div className="md:col-span-1 flex items-center justify-end">
                      {billItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBillItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {errors[`item_${index}`] && (
                      <div className="md:col-span-12">
                        <p className="text-red-500 text-sm">{errors[`item_${index}`]}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {errors.billItems && <p className="text-red-500 text-sm mt-2">{errors.billItems}</p>}
            </div>

            {/* Tax and Discount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax}
                  onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                  className={`input ${errors.tax ? 'border-red-500' : ''}`}
                />
                {errors.tax && <p className="text-red-500 text-sm mt-1">{errors.tax}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                  className={`input ${errors.discount ? 'border-red-500' : ''}`}
                />
                {errors.discount && <p className="text-red-500 text-sm mt-1">{errors.discount}</p>}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="input"
                placeholder="Additional notes..."
              />
            </div>

            {/* Totals Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-3">Invoice Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                {formData.tax > 0 && (
                  <div className="flex justify-between">
                    <span>Tax ({formData.tax}%):</span>
                    <span>${totals.taxAmount.toFixed(2)}</span>
                  </div>
                )}
                {formData.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount ({formData.discount}%):</span>
                    <span>-${totals.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 flex justify-between font-medium text-lg">
                  <span>Total:</span>
                  <span>${totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateBillingModal; 